import {useAnimatedPopper} from 'platform/components';
import {cn, cva} from 'platform/shared';

import {useEffect, useRef, useState, type FocusEvent, type KeyboardEvent} from 'react';

import {RequiredTestIdProps, suffixTestId, useOutsideClick} from 'shared/web';

import {Button} from './Button';

interface SelectProps extends RequiredTestIdProps {
  options: {
    label: string;
    value: string;
    isSelected?: boolean;
    onClick: () => void;
  }[];
  placeholder: string;
  value: string;
  tabIndex?: number;
}

const contentVariants = cva(
  'rounded-[var(--radius-small)] bg-[var(--color-palettes-black-900-70)] p-2 text-[var(--color-white)]'
);

export function Select(props: SelectProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const {referenceRef, togglePopper, popperProps, Popper, closePopper, isOpen} = useAnimatedPopper({
    placement: 'bottom-start',
    gutter: 4,
    isOutsideClickIgnored: true,
  });

  useEffect(
    function focusSelectedOptionOnOpen() {
      if (isOpen) {
        const selectedIndex = props.options.findIndex((option) => option.value === props.value);
        setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
      }
    },
    [isOpen, props.options, props.value]
  );

  useOutsideClick({
    ref,
    enabled: isOpen,
    handler: () => {
      closePopper();
    },
  });

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isOpen) {
      return;
    }

    const key = event.key;
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();

      const optionsCount = props.options.length;
      if (optionsCount === 0) {
        return;
      }

      if (key === 'ArrowDown') {
        setFocusedIndex((current) => (current + 1) % optionsCount);
      } else if (key === 'ArrowUp') {
        setFocusedIndex((current) => (current - 1 + optionsCount) % optionsCount);
      }
      return;
    }

    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      event.stopPropagation();

      const option = props.options.at(focusedIndex);
      if (option) {
        option.onClick();
        closePopper();
      }
    }
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!isOpen) {
      return;
    }

    // If the focus is on the popper (i.e. the option is clicked by mouse), don't close it
    const nextFocusedElement = event.relatedTarget;
    if (
      ref.current &&
      nextFocusedElement instanceof Node &&
      ref.current.contains(nextFocusedElement)
    ) {
      return;
    }

    closePopper();
  };

  return (
    <div ref={ref} onKeyDown={handleKeyDown} onBlur={handleBlur}>
      <div ref={referenceRef}>
        <Button
          isActive={isOpen}
          onClick={togglePopper}
          data-testid={suffixTestId('select', props)}
          title={
            props.options.find((option) => option.value === props.value)?.label ?? props.placeholder
          }
          tabIndex={props.tabIndex}
        />
      </div>
      <Popper {...popperProps}>
        <div className={cn(contentVariants())}>
          {props.options.map((option, index) => (
            <Button
              key={option.value}
              title={option.label}
              variant="ghost"
              size="small"
              isFullWidth
              justifyContent="start"
              isActive={isOpen ? index === focusedIndex : option.isSelected}
              onClick={() => {
                option.onClick();
                closePopper();
              }}
              tabIndex={-1}
              data-testid={suffixTestId(`selectOption-${option.value}`, props)}
            />
          ))}
        </div>
      </Popper>
    </div>
  );
}
